import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, Check, Link as LinkIcon, Trash2, Users, X, Ban, RotateCcw, Flag } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { avatarColor, avatarInitials } from "@/lib/avatar";
import { authHeaders } from "@/context/AuthContext";
import ReportConversationDialog from "@/components/ReportConversationDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Owner-only modal used to mint, rotate, and revoke a session's share link.
 *
 * Behaviour:
 *  - Toggling permission re-hits POST /sessions/{id}/share. When permission
 *    changes, the backend rotates the token so any older URL dies.
 *  - "Revoke link" calls DELETE → state returns to "no share".
 *  - Collaborators list is owner-only (GET /collaborators). Each row has a
 *    × button that hits DELETE /collaborators/{user_id}.
 */
export default function ShareSessionModal({ open, onOpenChange, sessionId, token, onShareChange }) {
    const { t } = useTranslation();
    const [permission, setPermission] = useState("view");
    const [shareUrl, setShareUrl] = useState("");
    const [shareToken, setShareToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [collaborators, setCollaborators] = useState([]);
    const [blockedIds, setBlockedIds] = useState([]);
    const [reportOpen, setReportOpen] = useState(false);
    const [confirmBlock, setConfirmBlock] = useState(null); // {user_id, name} | null

    const headers = token ? authHeaders(token) : {};

    const loadCollaborators = useCallback(async () => {
        if (!sessionId || !token) return;
        try {
            const [collabRes, blockedRes] = await Promise.all([
                axios.get(`${API}/sessions/${sessionId}/collaborators`, { headers }),
                axios
                    .get(`${API}/sessions/${sessionId}/blocked`, { headers })
                    .catch(() => ({ data: { blocked_user_ids: [] } })),
            ]);
            setCollaborators(collabRes.data?.collaborators || []);
            setBlockedIds(blockedRes.data?.blocked_user_ids || []);
        } catch (e) {
            console.warn("[share] collab fetch failed", e?.response?.status);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, token]);

    const createShare = useCallback(
        async (perm) => {
            if (!sessionId) return;
            setLoading(true);
            try {
                const r = await axios.post(
                    `${API}/sessions/${sessionId}/share`,
                    { permission: perm },
                    { headers },
                );
                setShareUrl(r.data.share_url);
                setShareToken(r.data.share_token);
                setPermission(r.data.permission);
                onShareChange?.({ token: r.data.share_token, permission: r.data.permission });
            } catch (e) {
                console.error(e);
                toast.error("Could not create share link.");
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sessionId, token, onShareChange],
    );

    // When the modal opens, auto-create a view-only link so the URL is
    // ready to copy without an extra click. If one already exists with the
    // same permission, the endpoint is idempotent.
    useEffect(() => {
        if (open) {
            createShare("view");
            loadCollaborators();
        } else {
            setCopied(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handlePermissionChange = useCallback(
        (next) => {
            if (next === permission) return;
            createShare(next);
        },
        [permission, createShare],
    );

    const handleCopy = useCallback(async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Copy failed — select the URL manually.");
        }
    }, [shareUrl]);

    const handleRevoke = useCallback(async () => {
        if (!sessionId) return;
        setLoading(true);
        try {
            await axios.delete(`${API}/sessions/${sessionId}/share`, { headers });
            setShareUrl("");
            setShareToken("");
            setCollaborators([]);
            onShareChange?.({ token: null, permission: null });
            toast.success("Share link revoked.");
            onOpenChange?.(false);
        } catch (e) {
            console.error(e);
            toast.error("Could not revoke.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, token, onShareChange, onOpenChange]);

    const handleKick = useCallback(
        async (userId) => {
            try {
                await axios.delete(`${API}/sessions/${sessionId}/collaborators/${userId}`, { headers });
                setCollaborators((c) => c.filter((x) => x.user_id !== userId));
            } catch {
                toast.error("Could not remove.");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sessionId, token],
    );

    const handleBlock = useCallback(
        async (userId) => {
            try {
                await axios.post(
                    `${API}/sessions/${sessionId}/collaborators/${userId}/block`,
                    {},
                    { headers },
                );
                setCollaborators((c) => c.filter((x) => x.user_id !== userId));
                setBlockedIds((b) => (b.includes(userId) ? b : [...b, userId]));
                setConfirmBlock(null);
                toast.success("Collaborator blocked.");
            } catch {
                toast.error("Could not block.");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sessionId, token],
    );

    const handleUnblock = useCallback(
        async (userId) => {
            try {
                await axios.delete(
                    `${API}/sessions/${sessionId}/collaborators/${userId}/block`,
                    { headers },
                );
                setBlockedIds((b) => b.filter((id) => id !== userId));
                toast.success("Unblocked.");
            } catch {
                toast.error("Could not unblock.");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sessionId, token],
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="share-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[17px]">
                        <LinkIcon size={16} strokeWidth={2} />
                        {t("share.modal_title")}
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-[#737373]">
                        {t("share.modal_subtitle")}
                    </DialogDescription>
                </DialogHeader>

                {/* Permission radios */}
                <div className="mt-3 space-y-2" role="radiogroup" aria-label="Share permission">
                    {[
                        { value: "view", titleKey: "share.permission_view", subKey: "share.permission_view_sub" },
                        { value: "edit", titleKey: "share.permission_edit", subKey: "share.permission_edit_sub" },
                    ].map((opt) => {
                        const active = permission === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => handlePermissionChange(opt.value)}
                                data-testid={`share-permission-${opt.value}`}
                                className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition ${
                                    active
                                        ? "border-[#0A0A0A] bg-[#F5F5F5]"
                                        : "border-[#E5E5E5] bg-white hover:border-[#0A0A0A]"
                                }`}
                            >
                                <span
                                    className={`mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${
                                        active ? "border-[#0A0A0A]" : "border-[#BFBFBF]"
                                    }`}
                                >
                                    {active && <span className="h-2 w-2 rounded-full bg-[#0A0A0A]" />}
                                </span>
                                <span className="flex-1">
                                    <span className="block text-[13.5px] font-medium text-[#0A0A0A]">
                                        {t(opt.titleKey)}
                                    </span>
                                    <span className="mt-0.5 block text-[12px] text-[#737373]">
                                        {t(opt.subKey)}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* URL row */}
                <div className="mt-4 flex items-center gap-2">
                    <input
                        readOnly
                        value={shareUrl}
                        data-testid="share-url-input"
                        className="flex-1 rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 font-mono text-[12.5px] text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                    />
                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!shareUrl || loading}
                        data-testid="share-copy-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3.5 py-2 text-[12.5px] font-medium text-white transition hover:opacity-90 disabled:opacity-40"
                    >
                        {copied ? <Check size={13} /> : <Copy size={13} />}
                        {copied ? t("share.copied") : t("share.copy")}
                    </button>
                </div>
                <p className="mt-2 text-[11px] leading-snug text-[#9CA3AF]" data-testid="share-hint">
                    {t("share.hint")}
                </p>

                {/* Collaborators (edit mode only shows the real value, but render empty-state for discoverability) */}
                <div className="mt-5 border-t border-[#E5E5E5] pt-4">
                    <h4 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#737373]">
                        <Users size={12} />
                        {t("share.collaborators_label")}
                    </h4>
                    {collaborators.length === 0 ? (
                        <p className="mt-2 text-[12.5px] text-[#9CA3AF]" data-testid="share-collab-empty">
                            {t("share.empty_collaborators")}
                        </p>
                    ) : (
                        <ul className="mt-2 space-y-1.5" data-testid="share-collab-list">
                            {collaborators.map((c) => (
                                <li
                                    key={c.user_id}
                                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-[#F5F5F5]"
                                >
                                    <span
                                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[10.5px] font-semibold text-white"
                                        style={{ backgroundColor: avatarColor(c.avatar_seed || c.user_id) }}
                                    >
                                        {avatarInitials(c.name)}
                                    </span>
                                    <span className="flex-1 truncate text-[13px] text-[#0A0A0A]">
                                        {c.name}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmBlock({ user_id: c.user_id, name: c.name })}
                                        aria-label={`Block ${c.name}`}
                                        title="Block this collaborator"
                                        data-testid={`block-collaborator-${c.user_id}`}
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-white hover:text-[#B91C1C]"
                                    >
                                        <Ban size={12} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleKick(c.user_id)}
                                        aria-label={t("share.kick")}
                                        data-testid={`share-kick-${c.user_id}`}
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-white hover:text-[#DC2626]"
                                    >
                                        <X size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {blockedIds.length > 0 && (
                        <div className="mt-3 border-t border-[#E5E5E5] pt-3" data-testid="blocked-section">
                            <h5 className="text-[11px] font-semibold uppercase tracking-wide text-[#737373]">
                                Blocked
                            </h5>
                            <ul className="mt-1.5 space-y-1">
                                {blockedIds.map((uid) => (
                                    <li
                                        key={uid}
                                        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5"
                                    >
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F5F5] text-[10px] text-[#737373]">
                                            <Ban size={11} />
                                        </span>
                                        <span className="flex-1 truncate font-mono text-[11.5px] text-[#737373]">
                                            {uid.slice(0, 8)}…
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleUnblock(uid)}
                                            data-testid={`unblock-collaborator-${uid}`}
                                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                                        >
                                            <RotateCcw size={10} />
                                            Unblock
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={handleRevoke}
                        disabled={!shareUrl || loading}
                        data-testid="share-revoke-btn"
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#DC2626] transition hover:opacity-70 disabled:opacity-40"
                    >
                        <Trash2 size={13} />
                        {t("share.revoke")}
                    </button>
                    {shareToken && (
                        <button
                            type="button"
                            onClick={() => setReportOpen(true)}
                            data-testid="share-report-btn"
                            className="inline-flex items-center gap-1.5 text-[12px] text-[#737373] transition hover:text-[#0A0A0A]"
                        >
                            <Flag size={11} />
                            Report this share session
                        </button>
                    )}
                </div>
            </DialogContent>
            {/* Block confirmation */}
            <Dialog open={!!confirmBlock} onOpenChange={(v) => !v && setConfirmBlock(null)}>
                <DialogContent className="max-w-sm border-[#E5E5E5] bg-white text-[#0A0A0A]">
                    <DialogHeader>
                        <DialogTitle className="text-[15px] font-semibold">
                            Block {confirmBlock?.name || "this collaborator"}?
                        </DialogTitle>
                        <DialogDescription className="text-[13px] leading-relaxed text-[#525252]">
                            They&apos;ll be removed from this conversation and prevented from
                            rejoining via this link. You can unblock them later.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setConfirmBlock(null)}
                            className="rounded-lg border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#0A0A0A] hover:bg-[#F5F5F5]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => handleBlock(confirmBlock.user_id)}
                            data-testid="confirm-block-button"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[#262626]"
                        >
                            <Ban size={11} /> Block
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
            <ReportConversationDialog
                open={reportOpen}
                onOpenChange={setReportOpen}
                shareToken={shareToken}
            />
        </Dialog>
    );
}
